// Putting a pending action back where it was raised.
//
// The design chose "inline in the conversation, at the point it was raised, because that is
// where the reader already is and it keeps the thread readable as a record of what
// happened".
//
// Which needs an anchor, and the contract carries exactly one: `createdAt`. A pending
// action has no message reference, and the runner writes the ask and the action in the
// same activation, so their timestamps sit together. So an action goes after the last
// message written no later than it was, and at the end when there is no such message.
//
// This is a rendering order and nothing else. It does not claim that any particular
// message raised any particular action; it puts the ask next to the passage of
// conversation it belongs to, which is what a reader needs. If a message reference is ever
// added to the view, this function is the one place that changes.
//
// Ties are broken toward the message, so an action stamped in the same millisecond as the
// facilitator's sentence appears under it rather than above it.
//
// ---------------------------------------------------------------------------
// SOMEBODY ELSE'S ASK HAS NO ANCHOR, AND GOES AT THE FOOT OF THE THREAD
// ---------------------------------------------------------------------------
//
// Since D70 the room sees that an ask is open and who it is with, and its `createdAt` is
// null — the server does not send one, and `contract.ts` would not hold one. So there is
// nothing to place it by, and it is placed at the end: that is the position that says "this
// is open now", which is the whole of what the room is entitled to know.
//
// The tempting repair is to anchor it to the last message anyway and take the time from
// there. That is the forbidden thing wearing a workaround: the messages around it carry
// visible timestamps, so anchoring another person's ask among them is publishing its age
// through the transcript. An ask at the foot of the thread carries no such claim.
//
// The order AMONG unanchored asks is the server's own, which is stable across refetches.
// It is an ordering and not a duration, and it is never interleaved with the timestamped
// messages, so nothing about how long any of them has been open can be read out of it.

import type { Message, PendingAction } from '../api/contract.js';

export type ThreadItem =
  | { kind: 'message'; key: string; message: Message }
  | { kind: 'action'; key: string; action: PendingAction };

export function interleave(
  messages: readonly Message[],
  actions: readonly PendingAction[],
): ThreadItem[] {
  const items: ThreadItem[] = messages.map((message) => ({
    kind: 'message',
    key: `m:${message.id}`,
    message,
  }));

  const anchored = actions.filter((action) => action.createdAt !== null);
  const ordered = [...anchored].sort((a, b) => {
    if (a.createdAt === b.createdAt) {
      return a.id < b.id ? -1 : 1;
    }
    return (a.createdAt ?? '') < (b.createdAt ?? '') ? -1 : 1;
  });

  for (const action of ordered) {
    const at = anchorFor(items, action.createdAt ?? '');
    items.splice(at, 0, { kind: 'action', key: `a:${action.id}`, action });
  }

  // The ones with no anchor, in the order the server listed them. See the header.
  for (const action of actions) {
    if (action.createdAt === null) {
      items.push({ kind: 'action', key: `a:${action.id}`, action });
    }
  }

  return items;
}

function anchorFor(items: readonly ThreadItem[], createdAt: string): number {
  let at = items.length;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (
      item !== undefined &&
      item.kind === 'message' &&
      item.message.createdAt <= createdAt
    ) {
      at = index + 1;
      break;
    }
    if (index === 0) {
      at = 0;
    }
  }
  // Past anything already anchored here. Two asks raised in the same breath keep the
  // order the facilitator raised them in; without this the later one lands first.
  while (items[at]?.kind === 'action') {
    at += 1;
  }
  return at;
}
